import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { ArrowRight, CheckCircle2, FileText, Users, Phone, Calendar, Briefcase, Settings, Rocket, BarChart3, TrendingUp, ClipboardCheck } from 'lucide-react';

const STEPS = [
  { n: 1, icon: FileText, title: 'Gratis scan starten', desc: 'U vult uw website in en wij scannen direct uw online vindbaarheid (volledig gratis).' },
  { n: 2, icon: ClipboardCheck, title: 'Directe analyse', desc: 'Binnen 60 seconden ontvangt u uw groeiscore en concrete actiepunten.' },
  { n: 3, icon: Phone, title: 'Adviesgesprek (optioneel)', desc: 'Wilt u onze hulp? We bespreken telefonisch uw doelen en het juiste pakket.' },
  { n: 4, icon: Briefcase, title: 'Pakket kiezen', desc: 'Connected Self-Service €14,95/mnd excl. btw of Managed AI Bot vanaf €995,95 excl. btw eerste maand.' },
  { n: 5, icon: FileText, title: 'Facturatie', desc: 'Maandelijks gefactureerd. Geen contract, maandelijks opzegbaar.' },
  { n: 6, icon: Users, title: 'Klantaccount aanmaken', desc: 'U krijgt toegang tot uw eigen klantdashboard met alle scan resultaten.' },
  { n: 7, icon: ClipboardCheck, title: 'Onboarding invullen', desc: 'Stap-voor-stap onboarding zodat wij u perfect kunnen ondersteunen.' },
  { n: 8, icon: Settings, title: 'Toegangen koppelen', desc: 'Advertentieaccounts, Analytics, Search Console en social platforms gekoppeld. U behoudt eigendom.' },
  { n: 9, icon: Calendar, title: 'Kick-off (Managed AI launch maand €995,95)', desc: 'Bij Managed AI is de eerste maand uw launch-maand met onboarding, audits en strategiesessie. Daarna gaat het maandelijkse tarief van €2.495,95 excl. btw in.' },
  { n: 10, icon: Rocket, title: 'Uitvoering', desc: 'U voert zelf uit met onze tips, óf onze AI + specialisten doen het voor u.' },
  { n: 11, icon: BarChart3, title: 'Rapportage via dashboard', desc: 'Real-time inzicht via uw klantdashboard. Wekelijkse en maandelijkse rapporten.' },
  { n: 12, icon: TrendingUp, title: 'Optimalisatie & groei', desc: 'Doorlopend bijsturen voor blijvende verbetering en groei.' },
];

export default function WerkwijzePage() {
  return (
    <>
      <SEOHead
        title="Werkwijze — Zo werkt IMPERIA AI"
        description="Zo werkt IMPERIA AI: van aanvraag, analyse en adviesgesprek tot onboarding, kick-off, uitvoering en doorlopende rapportage via het klantdashboard."
        canonical="/werkwijze"
        keywords="werkwijze marketingbureau, onboarding marketing, kick-off marketing, marketing proces"
      />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)', padding: '120px 0 80px', position: 'relative' }}>
        <div className="container-custom max-w-4xl mx-auto text-center px-4">
          <span style={{ color: '#D4A017', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Werkwijze</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6" style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.1 }}>
            Van scan tot <span style={{ color: '#D4A017' }}>groei</span> in 12 duidelijke stappen
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            Begin gratis met een online scan. Verbeter het zelf met onze tips, of laat onze AI + specialisten alles maandelijks regelen. Hieronder ziet u exact hoe wij werken — geen verborgen processen.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/register" data-testid="werkwijze-cta-offerte" className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-7 py-3.5 rounded-xl">
              Start gratis scan <ArrowRight size={16} />
            </Link>
            <Link to="/klantdashboard" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-7 py-3.5 rounded-xl">
              Bekijk klantdashboard
            </Link>
          </div>
        </div>
      </section>

      {/* Notice */}
      <section className="bg-[#fff8e1] border-y border-[#D4A017]/20 py-8">
        <div className="container-custom max-w-4xl mx-auto px-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#D4A017] flex items-center justify-center shrink-0">
              <CheckCircle2 size={20} className="text-[#0f172a]" />
            </div>
            <div>
              <p className="text-[#0f172a] font-bold text-sm md:text-base">
                Globale analyse vooraf — diepe strategie na akkoord
              </p>
              <p className="text-[#475569] text-sm md:text-base mt-1 leading-relaxed">
                Wij geven in het eerste gesprek een globale analyse van uw situatie. De volledige strategie en diepgaande analyse worden uitgewerkt ná akkoord, onboarding en kick-off — zodat het tijd en focus van onze strategen waard is.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 12 Steps */}
      <section className="bg-white py-20">
        <div className="container-custom max-w-5xl mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {STEPS.map(s => {
              const Icon = s.icon;
              return (
                <div key={s.n} data-testid={`werkwijze-step-${s.n}`} className="bg-[#f8fafc] border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:border-[#D4A017]/30 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0f172a] flex items-center justify-center">
                      <Icon size={18} className="text-[#D4A017]" />
                    </div>
                    <span className="font-bold text-[#D4A017] text-2xl">{String(s.n).padStart(2, '0')}</span>
                  </div>
                  <h3 className="font-bold text-[#0f172a] text-base mb-1.5">{s.title}</h3>
                  <p className="text-sm text-[#64748b] leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust block */}
      <section className="bg-[#f8fafc] py-16">
        <div className="container-custom max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0f172a] text-center mb-10" style={{ fontFamily: "'Sora', sans-serif" }}>
            Wat u van ons mag verwachten
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Reactie op uw aanvraag binnen 24 uur',
              'Volledige transparantie via uw klantdashboard',
              'Uw advertentieaccounts en data blijven uw eigendom',
              'Vaste contactpersoon bij IMPERIA AI',
              'Maandelijkse rapportage en evaluatie',
              'Geen verborgen kosten — alles wordt vooraf afgestemd',
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-5">
                <CheckCircle2 size={20} className="text-[#D4A017] shrink-0 mt-0.5" />
                <span className="text-[#0f172a] font-medium text-sm md:text-base">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f172a] py-20">
        <div className="container-custom max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
            Klaar voor de eerste stap?
          </h2>
          <p className="text-white/70 mb-8 text-base md:text-lg">
            Start gratis scan en ontvang binnen 24 uur reactie van uw persoonlijke adviseur.
          </p>
          <Link to="/register" data-testid="werkwijze-bottom-cta" className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-8 py-4 rounded-xl text-lg">
            Start gratis scan <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
